import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card } from "primereact/card";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { MultiSelect } from "primereact/multiselect";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

import { Ticket } from "../tickets/Ticket";
import { routes } from "../../common/routing/routes";
import {
  defaultPageSize,
  defaultPaginationOptions,
  emptyPaginationDetails,
  getPaginationFirst,
  getPaginationOptionsFromDataTable,
} from "../../common/api/paginationHelper";
import { searchAllTickets, searchAllTicketsIds } from "../tickets/ticketsApi";
import {
  ColumnDef,
  fetchVisibleColumns,
  persistVisibleColumns,
  columns,
  fetchFilters,
  persistFilters,
  clearPersistedVisibleColumns,
  clearPersistedFilters,
} from "./TicketsTableColumns";
import { selectAuthUser, selectProfile } from "../auth/authSlice";
import { canAccess } from "../auth/utils";
import { Privilege } from "../auth/Privilege";
import { useAppSelector } from "../../common/hooks/useStore";
import { selectDepartmentById } from "../departments/departmentsSlice";
import { useSkipFirstRender } from "../../common/hooks/useSkipFirstRender";
import TicketStatusButtons from "../ticketStatuses/TicketStatusButtons";
import TicketActions from "./TicketActions";
import NoResultsFound, { NoResultsFoundText } from "../../common/components/noResultsFound/NoResultsFound";
import TicketFiltersDropdown from "../ticketFilters/TicketFiltersDropdown";
import { TicketFilter } from "../ticketFilters/ticketFilter";
import { LS_LAST_USED_TICKET_FILTER_ID, LS_TICKETS_PAGE_OPTIONS } from "../../common/utils/common";

interface TicketsTableProps {
  departmentId: string;
}

export default function TicketsTable({ departmentId }: TicketsTableProps) {
  const navigate = useNavigate();
  const authUser = useSelector(selectAuthUser);
  const profile = useSelector(selectProfile);
  const department = useAppSelector((state) => selectDepartmentById(state, departmentId));
  const isAdmin = canAccess(Privilege.Admin, authUser.privilege);
  const paginationOptions = JSON.parse(localStorage.getItem(LS_TICKETS_PAGE_OPTIONS) || "{}");
  const pageNumber = paginationOptions?.pageNumber ?? emptyPaginationDetails.pageNumber;
  const pageSize = paginationOptions?.pageSize ?? defaultPageSize;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [relatedTicketIds, setRelatedTicketIds] = useState<string[]>([]);
  const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);
  const [pagination, setPagination] = useState(emptyPaginationDetails);
  const [first, setFirst] = useState(() => getPaginationFirst(pageNumber, pageSize));
  const [rows, setRows] = useState(paginationOptions.pageSize ?? defaultPageSize);
  const [filters, setFilters] = useState<any>(() => fetchFilters(profile.teamIds[0]));
  const [isLoading, setIsLoading] = useState(false);
  const agentsFilterTeamId = filters.teamId.value || profile.teamIds[0] || department?.defaultTeamId;
  const grandParentCategoryIds = filters.grandParentCategoryIds?.value;
  const parentCategoryIds = filters.parentCategoryIds?.value;
  const [availableCategoryIds, setAvailableCategoryIds] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<ColumnDef[]>(() =>
    fetchVisibleColumns({ departmentId, agentsFilterTeamId, grandParentCategoryIds, parentCategoryIds, availableCategoryIds: [] })
  );

  useEffect(() => {
    setIsLoading(true);
    fetchTickets();
    const intervalId = setInterval(fetchTickets, 1000 * 60);
    return () => clearInterval(intervalId);
  }, [first, rows, filters, departmentId]);

  useSkipFirstRender(() => {
    resetToDefaults();
  }, [departmentId]);

  const fetchTickets = async () => {
    if (departmentId) {
      const payload = {
        departmentId,
        id: filters.prettyId.value,
        subject: filters.subject.value,
        statuses: filters.status.value,
        urgency: filters.urgency.value,
        teamIds: filters.teamId.value,
        agentId: filters.agentId.value,
        locationIds: filters.location.value,
        ownerId: filters.ownerId?.value,
        grandParentCategoryIds: filters.grandParentCategoryIds?.value,
        parentCategoryIds: filters.parentCategoryIds?.value,
        categoryIds: filters.categoryIds?.value,
        createdDate: filters.createdDate?.value,
        sortParameters: [
          {
            columnName: "Created",
            isDescending: true,
          },
          {
            columnName: "SlaEndDate",
            isDescending: false,
          },
        ],
        ...getPaginationOptionsFromDataTable(first, rows),
      };
      const ticketsResponse = await searchAllTickets(payload);
      const ticketsIdsResponse = await searchAllTicketsIds(payload);
      const availableCategoryIds = [
        ...new Set(
          ticketsResponse.tickets.flatMap((t: Ticket) =>
            [t.grandParentCategory?.id, t.parentCategory?.id, t.category?.id].filter(Boolean)
          ) as string[]
        ),
      ];
      setAvailableCategoryIds(availableCategoryIds);
      setVisibleColumns(
        fetchVisibleColumns({ departmentId, agentsFilterTeamId, grandParentCategoryIds, parentCategoryIds, availableCategoryIds })
      );
      setPagination(ticketsResponse.pagination);
      setTickets(ticketsResponse.tickets);
      setRelatedTicketIds(ticketsIdsResponse);
      setSelectedTickets((prevSelectedTickets) => ticketsResponse.tickets.filter((t) => prevSelectedTickets.some((st) => st.id === t.id)));
      setIsLoading(false);
    }
  };

  const onSelectedTicketFilterChange = (selectedTicketFilter?: TicketFilter) => {
    if (selectedTicketFilter) {
      localStorage.setItem(LS_LAST_USED_TICKET_FILTER_ID, selectedTicketFilter.id);
      const newFilters = {
        ...filters,
        agentId: { ...filters.agentId, value: selectedTicketFilter?.agentId },
        location: { ...filters.location, value: selectedTicketFilter?.locationIds },
        teamId: { ...filters.teamId, value: selectedTicketFilter?.teamIds },
        status: { ...filters.status, value: selectedTicketFilter?.statusIds },
        grandParentCategoryIds: { ...filters.grandParentCategoryIds, value: selectedTicketFilter?.grandParentCategoryIds },
        parentCategoryIds: { ...filters.parentCategoryIds, value: selectedTicketFilter?.parentCategoryIds },
        categoryIds: { ...filters.categoryIds, value: selectedTicketFilter?.categoryIds },
      };
      setFilters(newFilters);
      persistFilters(newFilters);
      resetPagination();
    } else {
      resetToDefaults();
    }
  };

  const onTicketStatusesFilterChange = (ticketStatuses: string[] | undefined) => {
    const newFilters = { ...filters, status: { ...filters.status, value: ticketStatuses } };
    setFilters(newFilters);
    persistFilters(newFilters);
    resetPagination();
  };

  const onTicketActionFinished = () => {
    setSelectedTickets([]);
    fetchTickets();
  };

  const resetToDefaults = () => {
    localStorage.removeItem(LS_LAST_USED_TICKET_FILTER_ID);
    clearPersistedVisibleColumns();
    clearPersistedFilters();
    setAvailableCategoryIds([]);
    setVisibleColumns(
      fetchVisibleColumns({ departmentId, agentsFilterTeamId, grandParentCategoryIds, parentCategoryIds, availableCategoryIds: [] }, true)
    );
    setFilters(fetchFilters(profile.teamIds[0], true));
    resetPagination();
  };

  const resetPagination = () => {
    setPagination(emptyPaginationDetails);
    setFirst(getPaginationFirst(emptyPaginationDetails.pageNumber, defaultPageSize));
    setRows(defaultPageSize);
    localStorage.removeItem(LS_TICKETS_PAGE_OPTIONS);
  };

  const onPage = (event: DataTableStateEvent) => {
    setFirst(event.first);
    setRows(event.rows);
    localStorage.setItem(LS_TICKETS_PAGE_OPTIONS, JSON.stringify(getPaginationOptionsFromDataTable(event.first, event.rows)));
  };

  return (
    <div className="grid">
      <div className={`${selectedTickets.length ? "col-12 xl:col-8" : "col-12"}`}>
        <Card
          title={
            <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-2">
              <div className="text-2xl font-bold">Tichete</div>
              <div className="flex align-items-center justify-content-between w-full md:w-max gap-2">
                <TicketFiltersDropdown
                  className="w-12rem md:w-13rem lg:w-15rem"
                  departmentId={departmentId}
                  onChange={onSelectedTicketFilterChange}
                />
                <TicketStatusButtons inline value={filters.status.value} onChange={onTicketStatusesFilterChange}></TicketStatusButtons>
              </div>
            </div>
          }
        >
          <div className="flex flex-wrap gap-2 mb-4">
            <MultiSelect
              className="w-full md:w-10"
              display="chip"
              dataKey="header"
              optionLabel="header"
              options={columns({ departmentId, agentsFilterTeamId, grandParentCategoryIds, parentCategoryIds, availableCategoryIds })}
              value={visibleColumns}
              emptyMessage={NoResultsFoundText}
              onChange={(e) => {
                setVisibleColumns(e.value);
                persistVisibleColumns(e.value);
              }}
            />
            <Button type="button" text severity="secondary" onClick={() => resetToDefaults()}>
              Reseteaza
            </Button>
          </div>

          <DataTable
            pt={{
              column: {
                headerFilterClearButton: {
                  className: "display-none",
                },
              },
            }}
            rowClassName={() => "cursor-pointer"}
            size="small"
            lazy
            selectionMode="checkbox"
            selection={selectedTickets}
            paginator
            first={first}
            rows={rows}
            rowsPerPageOptions={defaultPaginationOptions}
            totalRecords={pagination.totalCount}
            filterDisplay="row"
            filters={filters}
            filterDelay={500}
            value={tickets}
            emptyMessage={<NoResultsFound />}
            loading={isLoading}
            onSelectionChange={(e) => {
              e.originalEvent.stopPropagation();
              setSelectedTickets(e.value);
            }}
            onPage={onPage}
            onFilter={(e) => {
              setFilters(e.filters);
              persistFilters(e.filters);
              resetPagination();
            }}
            onRowClick={(e) => {
              if (!(e.originalEvent.target as any)?.className.includes("p-selection-column")) {
                navigate(`${routes.agentTicketDetails.navigatePath}/${e.data.id}?relatedTicketIds=${relatedTicketIds.join(",")}`);
              }
            }}
          >
            {isAdmin && <Column className="w-3rem" selectionMode="multiple"></Column>}
            {visibleColumns.map((col, index) => col.column(`${index}`))}
          </DataTable>
        </Card>
      </div>
      {!!selectedTickets.length && (
        <div className="col-12 xl:col-4">
          <Card title={`Editeaza ${selectedTickets.length} tichete`}>
            <TicketActions
              className="mb-2"
              selectedTickets={selectedTickets.map((t) => ({
                ticketId: t.id,
                agentId: t.agentId,
              }))}
              onActionFinished={onTicketActionFinished}
              setIsLoading={setIsLoading}
              departmentId={departmentId}
            ></TicketActions>
          </Card>
        </div>
      )}
    </div>
  );
}
